import React, { useMemo } from 'react';
import {
  TableContainer, Table, Thead, Tbody, Tr, Th, Td, Box
} from '@chakra-ui/react';

import { getApproveResultNameByCode, ApproveResult, WorkflowId } from '../../contexts/constants';
import './ReportAndDraft.css'; // CSSファイルをインポート
import type { ApproverList } from '../../contexts/types';
import { APPROVAL_LIST_BOX_PREFIX } from './pdf/pdfConstants';
import { useTranslation } from 'react-i18next';
import { formatDateByLocale } from '../../utils/utils';

const AL = 'features.InvestmentScreening.ApprovalListComponent';

interface ApprovalListComponentProps {
  approverList: ApproverList[];
}

/**
 * 承認者一覧
 */
const ApprovalListComponent: React.FC<ApprovalListComponentProps> = ({ approverList }) => {
  const { t } = useTranslation();

  const flowIdToApprovalCategory: Record<string, string> = {
    [WorkflowId.ProductDirector]: t(`${AL}.category.productDirector`),
    [WorkflowId.BusinessManager]: t(`${AL}.category.businessManager`),
    [WorkflowId.CrossCheck]: t(`${AL}.category.crossCheck`),
    [WorkflowId.MaterialOfficerRequester]: t(`${AL}.category.materialOfficerRequester`),
    [WorkflowId.MaterialOfficer]: t(`${AL}.category.materialOfficer`),
    [WorkflowId.SalesHQManagerRequester]: t(`${AL}.category.salesHQManagerRequester`),
    [WorkflowId.SalesHQManager]: t(`${AL}.category.salesHQManager`),
    [WorkflowId.BusinessHQManagerRequester]: t(`${AL}.category.businessHQManagerRequester`),
    [WorkflowId.BusinessHQManager]: t(`${AL}.category.businessHQManager`),
    [WorkflowId.PreReview]: t(`${AL}.category.preReview`),
    [WorkflowId.HQReview]: t(`${AL}.category.hqReview`),
    [WorkflowId.PresidentReport]: t(`${AL}.category.presidentReport`),
  };

  // プレ検討会・本部長設備投資検討会・会長/副会長/社長確認は1行にまとめる
  const GROUPED_FLOW_IDS = new Set<string>([WorkflowId.PreReview, WorkflowId.HQReview, WorkflowId.PresidentReport]);
  const GROUPED_APPROVER_NAME = t(`${AL}.groupedApproverName`);

  const processedList = useMemo(() => {
    const result: typeof approverList = [];
    const seenFlowIds = new Set<string>();

    for (const approver of approverList) {
      if (approver.flowId && GROUPED_FLOW_IDS.has(approver.flowId)) {
        if (!seenFlowIds.has(approver.flowId)) {
          seenFlowIds.add(approver.flowId);
          // 承認済みの担当者を探して結果・日時・コメントを代表値として使用
          // 差戻し優先: 差戻しあればそちら、なければ承認済みを使用
          const remandOne = approverList.find(
            a => a.flowId === approver.flowId && a.confirm === true && a.approvalResult === ApproveResult.Remand.code
          );
          const approvedOne = remandOne ?? approverList.find(
            a => a.flowId === approver.flowId && a.confirm === true
          );
          result.push({
            ...approver,
            employeeName: GROUPED_APPROVER_NAME,
            organizationName: "-",
            positionName: "-",
            approvalResult: approvedOne?.approvalResult ?? undefined,
            confirmTime: approvedOne?.confirmTime ?? undefined,
            comment: approvedOne?.comment ?? undefined,
          });
        }
        // 2件目以降はスキップ（1行にまとめる）
      } else {
        result.push(approver);
      }
    }
    return result;
  }, [approverList, t]);

  const getStatusClass = (status: string) => {
    switch (status) {
      case ApproveResult.Approval.code:
        return "statusbox statusbox-accept";
      case ApproveResult.Remand.code:
        return "statusbox statusbox-reject";
      case ApproveResult.Cancel.code:
        return "statusbox statusbox-cancel";
      default:
        return "statusbox";
    }
  };

  // 表示する行のインデックスを管理
  let displayIndex = 0;

  const flowIdCounts = processedList.reduce((acc, approver) => {
    if (approver.flowId != null) {
      acc[approver.flowId] = (acc[approver.flowId] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  // flowIdごとの最初の出現位置を記録
  const firstOccurrence: Record<string, number> = {};
  processedList.forEach((approver, index) => {
    if (approver.flowId != null && firstOccurrence[approver.flowId] == null) {
      firstOccurrence[approver.flowId] = index;
    }
  });


  return (
    <Box>
      <Box width="100%">
        <TableContainer border="1px solid" borderColor="gray.200" height="100%" overflowY="unset">
          <Table
            id={`${APPROVAL_LIST_BOX_PREFIX}`}
            border="1px solid"
            borderColor="gray.200"
            variant="simple"
          >
            <Thead position="sticky" top={0} boxShadow={`0 -1px 0 #EEEEEE`}>
              <Tr borderRight="1px solid" borderColor="gray.200" bgColor="gray.100">
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="50px" textAlign="center">No.</Th>
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="15vw" textAlign="center">{t(`${AL}.col.approver`)}</Th>
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="12vw" textAlign="center">{t(`${AL}.col.approvalCategory`)}</Th>
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="12vw" textAlign="center">{t(`${AL}.col.department`)}</Th>
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="10vw" textAlign="center">{t(`${AL}.col.position`)}</Th>
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="10vw" textAlign="center">{t(`${AL}.col.result`)}</Th>
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="20vw" textAlign="center">{t(`${AL}.col.confirmedAt`)}</Th>
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="50vw" textAlign="center">{t(`${AL}.col.comment`)}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {processedList.map((approver, index) => {
                const isFirstOccurrence = approver.flowId != null && firstOccurrence[approver.flowId] === index;
                if (isFirstOccurrence) { displayIndex += 1; }
                return (
                  <Tr key={index}>
                    {isFirstOccurrence && approver.flowId != null && (
                      <Td
                        borderRight="1px solid"
                        borderColor="gray.200"
                        rowSpan={flowIdCounts[approver.flowId]}
                      >
                        {displayIndex}
                      </Td>
                    )}
                    <Td borderRight="1px solid" borderColor="gray.200">
                      {approver.employeeName}
                    </Td>
                    <Td borderRight="1px solid" borderColor="gray.200">
                      {approver.flowId === WorkflowId.CrossCheck && approver.categoryName
                        ? t(`${AL}.category.crossCheckWithName`, { name: approver.categoryName })
                        : approver.flowId ? (flowIdToApprovalCategory[approver.flowId] || "") : ""}
                    </Td>
                    <Td borderRight="1px solid" borderColor="gray.200">
                      {approver.organizationName}
                    </Td>
                    <Td borderRight="1px solid" borderColor="gray.200">
                      {approver.positionName}
                    </Td>
                    <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                      {approver.approvalResult != null && (
                        <span className={getStatusClass(approver.approvalResult)}>
                          {getApproveResultNameByCode(approver.approvalResult)}
                        </span>
                      )}
                    </Td>
                    <Td borderRight="1px solid" borderColor="gray.200">
                      {approver.confirmTime ? formatDateByLocale(approver.confirmTime, true, false, "-") : ''}
                    </Td>
                    <Td borderRight="1px solid" borderColor="gray.200" whiteSpace="pre-wrap">
                      {approver.comment}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default ApprovalListComponent;